/* ==========================================================================
   SOPHIE CONRAN - LEAD FIELD NORMALISER
   --------------------------------------------------------------------------
   One cleanup pass over what a visitor types into a lead form, run before it
   reaches Klaviyo, n8n, Freshsales, the Google Sheet or KV. Two fields:

     the address  a mistyped one is the quiet way a lead disappears. The
                  contact is never created, or it is created and never
                  receives anything.
     the name     it is merged into the greeting of every email that follows,
                  and "Dear JANE" undoes the tone of the whole message.

   The file keeps its email-era name because it is already deployed under it.

   THIS FILE IS THE CANONICAL COPY. The Worker copy at
   sc-saves-worker/src/email-normalise.js is generated from it by
   tools/sync-email-normalise.js. Edit here, then run that script.

   Three tiers, in order of how sure we are:

     1. Tidy      always applied, and it never changes who the mail reaches.
                  Whitespace, mailto:, angle brackets, stray commas, doubled
                  dots, trailing punctuation, case.
     2. Repair    applied automatically. A typed domain that cannot receive
                  mail at all, mapped to the one it plainly meant: a broken
                  TLD (.con, .cmo, .ocm) or a one keystroke slip on a known
                  mailbox provider (gnail.com, hotmai.com).
     3. Suggest   never applied on its own. A plausible but less certain
                  match, returned as `suggestion` for the form to offer and
                  for the team to review.

   Nothing is ever thrown away: when the address changes, the original is
   returned as `original` and is stored alongside it.

   Usage (browser):  SC.email.normalise('joe@gnail.con')
   Usage (Worker):   import { normaliseEmail } from './email-normalise.js';

   Returns:
     {
       email:       cleaned address, or '' when unusable
       original:    exactly what was passed in, trimmed
       valid:       whether `email` is a well formed address
       changed:     whether `email` differs from `original`
       corrections: array of short tags, eg ['tidy', 'tld:con>com']
       suggestion:  a tier 3 alternative, or '' when there is none
     }
   ========================================================================== */

(function (root) {
  'use strict';

  /* Mailbox providers we recognise.
     Two jobs. A domain in this list is never corrected, however close it
     sits to another (mail.com is one keystroke from gmail.com and is a real
     provider). And it is the only set a tier 2 repair may correct towards,
     so a typo can never be pulled onto some domain we have never heard of. */
  var PROVIDERS = [
    'gmail.com', 'googlemail.com',
    'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.it', 'hotmail.es', 'hotmail.de',
    'outlook.com', 'outlook.co.uk', 'live.com', 'live.co.uk', 'msn.com',
    'yahoo.com', 'yahoo.co.uk', 'yahoo.ca', 'yahoo.com.au', 'yahoo.fr', 'yahoo.de', 'yahoo.es',
    'ymail.com', 'rocketmail.com',
    'icloud.com', 'me.com', 'mac.com',
    'aol.com', 'aol.co.uk',
    'btinternet.com', 'btconnect.com', 'sky.com', 'virginmedia.com', 'talktalk.net',
    'ntlworld.com', 'blueyonder.co.uk', 'tiscali.co.uk', 'btopenworld.com',
    'tesco.net', 'plus.net',
    'gmx.com', 'gmx.co.uk', 'gmx.de', 'mail.com', 'email.com',
    'protonmail.com', 'proton.me', 'pm.me', 'zoho.com', 'fastmail.com', 'hey.com',
    'comcast.net', 'verizon.net', 'sbcglobal.net', 'att.net', 'bellsouth.net',
    'cox.net', 'charter.net', 'earthlink.net', 'optonline.net', 'juno.com',
    'shaw.ca', 'rogers.com', 'sympatico.ca', 'bigpond.com', 'optusnet.com.au',
    'web.de', 't-online.de', 'wanadoo.fr', 'orange.fr', 'free.fr', 'laposte.net',
    'libero.it', 'sfr.fr', 'bluewin.ch'
  ];

  var PROVIDER_SET = {};
  for (var pi = 0; pi < PROVIDERS.length; pi++) PROVIDER_SET[PROVIDERS[pi]] = true;

  /* Tier 2: broken top level domains.
     Applied to any domain, not only the providers above. sophieconran.con
     cannot receive mail either, and a business address is the one we can
     least afford to lose. Keys are what was typed, values what it meant. */
  var TLD_FIXES = {
    'con': 'com', 'cmo': 'com', 'ocm': 'com', 'cim': 'com', 'clm': 'com',
    'cpm': 'com', 'vom': 'com', 'xom': 'com', 'som': 'com', 'dom': 'com',
    'comm': 'com', 'commm': 'com', 'ccom': 'com', 'coom': 'com', 'como': 'com',
    'comn': 'com', 'comj': 'com', 'coml': 'com', 'coma': 'com', 'comq': 'com',
    /* .co is deliberately absent: it is a real and popular TLD, so
       company.co must survive. gmail.co and icloud.co are still repaired,
       by the provider rules further down. .om and .cm are kept because both
       are restricted registries we will never see a customer on, and both
       are classic slips off .com. */
    'c0m': 'com', 'cm': 'com', 'om': 'com', 'cok': 'com',
    'nte': 'net', 'ner': 'net', 'met': 'net', 'nett': 'net', 'ney': 'net',
    'ogr': 'org', 'orh': 'org', 'orgg': 'org', 'prg': 'org', 'ord': 'org',
    'couk': 'co.uk', 'ik': 'uk', 'yk': 'uk',
    'edy': 'edu'
  };

  /* Multi label endings, matched on the last two labels before the generic
     TLD pass. UK addresses collect these: co.ukk, co.ul, cu.uk, uk.co. */
  var SUFFIX_FIXES = {
    'co.ukk': 'co.uk', 'co.ul': 'co.uk', 'co.uj': 'co.uk', 'co.yk': 'co.uk',
    'co.ik': 'co.uk', 'cco.uk': 'co.uk', 'c0.uk': 'co.uk', 'vo.uk': 'co.uk',
    'xo.uk': 'co.uk', 'cu.uk': 'co.uk', 'co.k': 'co.uk', 'o.uk': 'co.uk',
    'uk.co': 'co.uk', 'com.uk': 'co.uk',
    'com.com': 'com', 'com.co': 'com', 'com.cm': 'com', 'co.com': 'com'
  };

  /* Tier 2: named domain slips a distance check will not catch.
     Mostly two or more keystrokes out, or a real domain that is simply not
     where the visitor has a mailbox (gmail.co.uk hosts no mailboxes). */
  var DOMAIN_FIXES = {
    'gmail.co.uk': 'gmail.com', 'gmail.us': 'gmail.com', 'gmail.uk': 'gmail.com',
    'gmaill.com': 'gmail.com', 'gmmail.com': 'gmail.com', 'gemail.com': 'gmail.com',
    'gmailc.om': 'gmail.com', 'gmail.c.om': 'gmail.com', 'gmail.comcom': 'gmail.com',
    'gmaol.com': 'gmail.com', 'gmailk.com': 'gmail.com', 'g-mail.com': 'gmail.com',
    'googemail.com': 'googlemail.com',
    'hotmail.co': 'hotmail.com', 'hotmial.co.uk': 'hotmail.co.uk',
    'hotmaill.com': 'hotmail.com', 'hotmail.couk': 'hotmail.co.uk',
    'hotamil.com': 'hotmail.com', 'hoymail.com': 'hotmail.com',
    'hotmailc.om': 'hotmail.com', 'hotmail.om': 'hotmail.com',
    'outlok.com': 'outlook.com', 'outllook.com': 'outlook.com',
    'outlook.co': 'outlook.com', 'outloook.com': 'outlook.com',
    'yaho.com': 'yahoo.com', 'yahooo.com': 'yahoo.com', 'yhaoo.com': 'yahoo.com',
    'yahoo.co': 'yahoo.com', 'yahoo.couk': 'yahoo.co.uk', 'yaoo.com': 'yahoo.com',
    'icloud.co': 'icloud.com', 'iclould.com': 'icloud.com', 'icloud.co.uk': 'icloud.com',
    'iclod.com': 'icloud.com',
    'btinternet.co.uk': 'btinternet.com', 'btintenet.com': 'btinternet.com',
    'btinernet.com': 'btinternet.com', 'btconnect.co.uk': 'btconnect.com',
    'sky.co.uk': 'sky.com', 'virginmedia.co.uk': 'virginmedia.com',
    'talktalk.co.uk': 'talktalk.net', 'ntlworld.co.uk': 'ntlworld.com',
    'aol.co': 'aol.com', 'aoll.com': 'aol.com',
    'live.co': 'live.com',
    'protonmail.co': 'protonmail.com', 'protonmial.com': 'protonmail.com',
    'comcast.com': 'comcast.net', 'verizon.com': 'verizon.net',
    'sbcglobal.com': 'sbcglobal.net', 'att.com': 'att.net'
  };

  /* Characters that carry no meaning inside an address and are almost always
     a paste artefact: zero width spaces, the BOM, non breaking space. */
  var INVISIBLE = /[​-‍⁠﻿ ]/g;

  /* Deliberately looser than RFC 5322 and stricter than reality. It matches
     what mailbox providers actually accept, which is the only test that
     decides whether a lead receives anything. */
  var SHAPE = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,24}$/;

  /* Damerau Levenshtein, so a transposition counts as one keystroke rather
     than two. Nearly every real typo is a transposition (gmial, gamil,
     hotmial), and plain Levenshtein scores those as 2 and so misses them. */
  function distance(a, b, ceiling) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > ceiling) return ceiling + 1;
    var prev2 = [], prev = [], cur = [], i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur = [i];
      var best = i;
      for (j = 1; j <= b.length; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var v = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 &&
            a.charAt(i - 1) === b.charAt(j - 2) &&
            a.charAt(i - 2) === b.charAt(j - 1)) {
          v = Math.min(v, prev2[j - 2] + 1);
        }
        cur[j] = v;
        if (v < best) best = v;
      }
      if (best > ceiling) return ceiling + 1;
      prev2 = prev; prev = cur;
    }
    return prev[b.length];
  }

  /* Closest provider domain, or '' when nothing is near enough.
     A `ceiling` of 1 is tier 2 (applied), 2 is tier 3 (suggested only).
     The first character must match and the length must be within one:
     without those guards a short domain drifts onto an unrelated provider. */
  function nearestProvider(domain, ceiling) {
    if (PROVIDER_SET[domain]) return '';
    var bestName = '', bestScore = ceiling + 1;
    for (var i = 0; i < PROVIDERS.length; i++) {
      var p = PROVIDERS[i];
      if (p.charAt(0) !== domain.charAt(0)) continue;
      if (Math.abs(p.length - domain.length) > 1) continue;
      var d = distance(domain, p, ceiling);
      if (d > 0 && d < bestScore) { bestScore = d; bestName = p; }
    }
    return bestScore <= ceiling ? bestName : '';
  }

  /* Tier 1. Everything here is safe: it cannot change which mailbox the
     address points at, only how it is written. */
  function tidy(raw) {
    var s = String(raw == null ? '' : raw);
    s = s.replace(INVISIBLE, '').replace(/\s+/g, ' ').trim();
    s = s.replace(/^mailto:\s*/i, '');
    /* A display name in front of the address: Joe Bloggs <joe@x.com> */
    var angled = s.match(/<([^<>]+)>\s*$/);
    if (angled) s = angled[1];
    s = s.replace(/^[<("'[]+/, '').replace(/[>)"'\]]+$/, '');
    s = s.replace(/\s+/g, '');
    /* Typed out or fullwidth at signs, only when there is no real one. */
    if (s.indexOf('@') === -1) {
      s = s.replace(/＠/g, '@').replace(/\((?:at)\)/gi, '@').replace(/\[at\]/gi, '@');
    }
    s = s.replace(/@{2,}/g, '@');
    s = s.replace(/[.,;:]+$/, '');
    return s;
  }

  function result(original, email, valid, corrections, suggestion) {
    return {
      email: email,
      original: original,
      valid: valid,
      changed: !!email && email !== original.toLowerCase(),
      corrections: corrections,
      suggestion: suggestion
    };
  }

  function normaliseEmail(raw) {
    var original = String(raw == null ? '' : raw).trim();
    var corrections = [];
    var s = tidy(original);
    if (s !== original) corrections.push('tidy');

    var at = s.lastIndexOf('@');
    if (at < 1 || at === s.length - 1) {
      return result(original, '', false, corrections, '');
    }

    var local = s.slice(0, at);
    var domain = s.slice(at + 1).toLowerCase();

    /* Local part: providers treat it case insensitively in practice, and
       Klaviyo keys profiles on the lowercased address, so matching that
       here is what stops one person becoming two contacts. */
    var lowerLocal = local.toLowerCase();
    if (lowerLocal !== local) { local = lowerLocal; corrections.push('case'); }
    local = local.replace(/^\.+/, '').replace(/\.+$/, '').replace(/\.{2,}/g, '.');

    /* Domain punctuation. A comma for a full stop is the commonest of all,
       because the two keys sit next to each other. */
    var before = domain;
    domain = domain.replace(/,/g, '.').replace(/\.{2,}/g, '.')
                   .replace(/^[.-]+/, '').replace(/[.-]+$/, '');
    if (domain !== before) corrections.push('domain-punctuation');

    /* Tier 2, most specific rule first so a named fix wins over a guess. */
    var fixed = DOMAIN_FIXES[domain];
    if (fixed) { corrections.push('domain:' + domain + '>' + fixed); domain = fixed; }

    if (!PROVIDER_SET[domain]) {
      var labels = domain.split('.');
      if (labels.length >= 3) {
        var last2 = labels.slice(-2).join('.');
        if (SUFFIX_FIXES[last2]) {
          var sfx = SUFFIX_FIXES[last2];
          corrections.push('suffix:' + last2 + '>' + sfx);
          domain = labels.slice(0, -2).concat(sfx.split('.')).join('.');
          labels = domain.split('.');
        }
      }
      if (labels.length >= 2) {
        var tld = labels[labels.length - 1];
        var t2 = TLD_FIXES[tld];
        if (t2 && t2 !== tld) {
          corrections.push('tld:' + tld + '>' + t2);
          domain = labels.slice(0, -1).concat(t2.split('.')).join('.');
        }
      }
    }

    var near = nearestProvider(domain, 1);
    if (near) { corrections.push('provider:' + domain + '>' + near); domain = near; }

    var email = local + '@' + domain;
    var valid = SHAPE.test(email) && email.length <= 254 && local.length <= 64;

    /* Tier 3. Offered, never applied: at two keystrokes out the guess is
       often right, but not right enough to redirect somebody's post. */
    var suggestion = '';
    if (valid) {
      var maybe = nearestProvider(domain, 2);
      if (maybe) suggestion = local + '@' + maybe;
    }

    return result(original, valid ? email : '', valid, corrections, suggestion);
  }

  /* ── Names ────────────────────────────────────────────────────────────────
     Same principle as the address, and the same one rule doing the work:
     act only where the visitor cannot have meant it.

     A name typed entirely in one case carries no capitalisation decision at
     all, so re-casing it destroys nothing. A name with any mixed case does
     carry one, and is left completely alone. That single guard is what
     protects McDonald, MacLeod, van der Berg, DeAngelo, d'Arcy and le Roux:
     each of them survives untouched, because each is already mixed.

     What it cannot do is recover a decision that was never typed. MACDONALD
     becomes Macdonald, not MacDonald, because nothing in the string says
     which it was. That is accepted: Macdonald reads as a name, MACDONALD
     reads as shouting.

     Particles are deliberately not special-cased. VAN DER BERG becomes Van
     Der Berg rather than van der Berg. A particle list would start
     lowercasing words some people capitalise on purpose, which is the kind
     of confident wrongness this whole file is built to avoid, and the
     greeting almost always uses the first name anyway. */

  var HAS_LETTER = /\p{L}/u;

  /* Runs are delimited by spaces, hyphens and apostrophes alike, so the
     letter after a hyphen or an apostrophe is capitalised as well:
     MARY-JANE to Mary-Jane, O'BRIEN to O'Brien. Both fall out of the one
     rule rather than needing a case each. */
  function titleCase(s) {
    return s.replace(/[^\s'’-]+/gu, function (run) {
      return run.charAt(0).toUpperCase() + run.slice(1).toLowerCase();
    });
  }

  function normaliseName(raw) {
    var original = String(raw == null ? '' : raw).trim();
    var corrections = [];

    var s = original.replace(INVISIBLE, '').replace(/\s+/g, ' ').trim();
    if (s !== original) corrections.push('tidy');

    /* Nothing with a letter in it means nothing to re-case. */
    if (!HAS_LETTER.test(s)) {
      return { name: s, original: original, changed: s !== original, corrections: corrections };
    }

    var isUpper = s === s.toUpperCase() && s !== s.toLowerCase();
    var isLower = s === s.toLowerCase() && s !== s.toUpperCase();

    if (isUpper || isLower) {
      var cased = titleCase(s);
      if (cased !== s) {
        corrections.push(isUpper ? 'case:upper' : 'case:lower');
        s = cased;
      }
    }

    return { name: s, original: original, changed: s !== original, corrections: corrections };
  }

  /* Browser wiring.
     Every lead form on the site reads its own field inside its own submit
     handler. Rather than edit each one, this listens on the document in the
     capture phase, which runs before any handler bound to the form itself,
     and rewrites the field in place. One hook, and a form added later is
     covered without anybody remembering this file exists.

     Tier 3 is not applied here either. It is shown under the field as a
     question, and only a click accepts it. */
  /* Named explicitly rather than matched loosely. A selector like
     [name*="name"] would also catch fields that are not names at all, and
     re-casing the wrong field is worse than missing one.

     Two naming conventions are in play. Our own forms use plain camelCase
     fields; anything built on a Shopify form (contact, register, account
     addresses) uses the bracketed convention, and those are easy to miss
     because they do not look like the others.

     This list is DOM field names. Its opposite number in the Worker,
     NAME_FIELDS in email-ingress.js, lists JSON keys, so the two overlap
     without being identical: the contact form posts contact[name] from a
     field but sends it as `name`. Add to both when adding a form. */
  var NAME_SELECTOR = 'input[name="firstName"], input[name="lastName"], ' +
                      'input[name="fullName"], input[name="name"], ' +
                      'input[name="first_name"], input[name="last_name"], ' +
                      'input[name="contact[name]"], ' +
                      'input[name="customer[first_name]"], ' +
                      'input[name="customer[last_name]"], ' +
                      'input[name="address[first_name]"], ' +
                      'input[name="address[last_name]"]';

  function visible(list) {
    return Array.prototype.slice.call(list).filter(function (el) {
      return el.type !== 'hidden' && !el.disabled;
    });
  }

  function fields(form) {
    return visible(form.querySelectorAll('input[type="email"], input[name="email"]'));
  }

  function nameFields(form) {
    return visible(form.querySelectorAll(NAME_SELECTOR)).filter(function (el) {
      return el.type === 'text' || el.type === 'search' || !el.type;
    });
  }

  function clean(el) {
    if (!el.value) return null;
    var r = normaliseEmail(el.value);
    if (r.email && r.email !== el.value) el.value = r.email;
    return r;
  }

  /* The name has to be corrected in the browser as well as in the Worker,
     not only for the visitor's sake: the catalogue form posts its Klaviyo
     profile straight from the page, so a name left in caps here reaches the
     greeting of the email whatever the Worker does afterwards. */
  function cleanName(el) {
    if (!el.value) return null;
    var r = normaliseName(el.value);
    if (r.name && r.name !== el.value) el.value = r.name;
    return r;
  }

  function hint(el, r) {
    var host = el.parentNode;
    if (!host) return;
    var existing = host.querySelector('.sc-email-hint');
    if (existing) host.removeChild(existing);
    if (!r || !r.suggestion) return;
    var box = document.createElement('div');
    box.className = 'sc-email-hint';
    box.appendChild(document.createTextNode('Did you mean '));
    var yes = document.createElement('button');
    yes.type = 'button';
    yes.className = 'sc-email-hint-yes';
    yes.textContent = r.suggestion;
    box.appendChild(yes);
    box.appendChild(document.createTextNode('?'));
    yes.addEventListener('click', function () {
      el.value = r.suggestion;
      if (box.parentNode) box.parentNode.removeChild(box);
      el.dispatchEvent(new Event('change', { bubbles: true }));
    });
    host.appendChild(box);
  }

  function bind() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      fields(form).forEach(function (el) { clean(el); });
      nameFields(form).forEach(function (el) { cleanName(el); });
    }, true);

    document.addEventListener('blur', function (e) {
      var el = e.target;
      if (!el || el.tagName !== 'INPUT') return;
      if (el.type !== 'email' && el.name !== 'email') return;
      hint(el, clean(el));
    }, true);
  }

  root.SC = root.SC || {};
  root.SC.email = { normalise: normaliseEmail, providers: PROVIDERS };
  root.SC.name = { normalise: normaliseName };

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bind);
    } else {
      bind();
    }
  }
/* globalThis first so the same file runs unchanged inside the Worker, where
   there is no `window` and a module's top level `this` is undefined. */
})(typeof globalThis !== 'undefined' ? globalThis : this);
